import styled from '@emotion/styled';
import {
  Prismic,
  PrismicLink,
  useInlineExpressions,
} from '@landsbankinn/prismic';
import {
  Row,
  Column,
  Divider,
  device,
  typography,
  Reveal,
  mobileGutterMargin,
} from '@landsbankinn/ui';
import React from 'react';
import type { ImgSource, ImgAr } from '../img/Img';
import { Img } from '../img/Img';

interface IProps {
  image: ImgSource;
  ar?: ImgAr;
  title: React.ReactNode;
  copy: React.ReactNode;
  insurance?: string;
  children: React.ReactNode;
  link?: Prismic.Link;
}

const Host = styled(Reveal)`
  padding: 45px 20px;
  margin: ${(props) => `6em -${props.theme.containerGutter}px 6em`};

  background-color: ${(props) => props.theme.colors.backgroundAlt};

  ${mobileGutterMargin}

  @media ${device.tablet} {
    padding: 45px;
    margin: 20px 0;
  }
`;

const Spacer = styled.div`
  padding-bottom: 40px;
  @media ${device.tablet} {
    padding-bottom: 0;
  }
`;

const ImageWrap = styled.div`
  margin-top: -100px;

  @media ${device.tablet} {
    position: relative;
    top: 0;
    left: 0;
    max-width: auto;
    margin: 0 -25px 0 -45px;
    margin-bottom: -50px;
  }

  @media ${device.desktop} {
    margin: 0 -25px 0 -45px;
  }
`;

const Text = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h2`
  ${typography.h3}
`;

const Copy = styled.div``;

const Table = styled.div`
  border-top: ${(props) => `1px solid ${props.theme.colors.foreground}`};
`;

const Item = styled.div`
  display: flex;
  flex-wrap: row nowrap;
  justify-content: space-between;

  padding: 11px 0 9px;

  border-bottom: ${(props) => `1px solid ${props.theme.colors.foreground}`};
`;

const Link = styled.a`
  &:hover p {
    text-decoration: underline;
  }
`;

const ItemSubTitle = styled.p`
  text-align: right;
`;

const PointsHost = styled.div`
  display: flex;
  align-items: center;
`;

const Point = styled.div<{ filled: boolean }>`
  width: 13px;
  height: 13px;
  margin-left: 9px;

  background-color: ${(props) =>
    props.filled ? props.theme.colors.primary : 'transparent'};

  border-radius: 20px;
  border: 1px solid ${(props) => props.theme.colors.primary};
`;

const Points = ({ number }: any) => {
  const count = Number(number);
  return (
    <PointsHost>
      <Point filled={count > 0} />
      <Point filled={count > 1} />
      <Point filled={count > 2} />
      <Point filled={count > 3} />
      <Point filled={count > 4} />
    </PointsHost>
  );
};

export const InfoCard = ({
  image,
  ar,
  title,
  copy,
  children,
  insurance,
  link,
}: IProps) => {
  const isImage = image;
  return (
    <Host>
      <Row>
        {isImage && (
          <Column md={{ width: 4 / 12 }}>
            <Spacer>
              <ImageWrap>
                <Img src={image} ar={ar} percentage={0.33} />
              </ImageWrap>
            </Spacer>
          </Column>
        )}
        <Column md={{ width: !isImage ? 7 / 12 : 3 / 12 }}>
          <Text>
            <Title>{title}</Title>
            <Divider />
            <Copy>{copy}</Copy>
          </Text>
        </Column>
        <Column md={{ width: 4 / 12, offset: 1 / 12 }}>
          <Table>
            {insurance !== '0' && (
              // eslint-disable-next-line react/jsx-no-undef
              <PrismicLink value={link} title="Ferðatryggingar">
                <Link>
                  <Item>
                    <p>Ferðatryggingar</p>
                    <Points number={insurance} />
                  </Item>
                </Link>
              </PrismicLink>
            )}
            {children}
          </Table>
        </Column>
      </Row>
    </Host>
  );
};

export const ListItem = ({ title, subtitle }: any) => {
  const source = useInlineExpressions(Prismic.text(subtitle) ?? undefined);
  return (
    <Item>
      <p>{title}</p>
      <ItemSubTitle>{source}</ItemSubTitle>
    </Item>
  );
};
